import { Box, Button, Inline, Stack, Text } from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { useState } from "react"
import { TAutomationRetryMode, TAutomationRun, XHR } from "./store"

function retryModeLabel(mode: TAutomationRetryMode) {
	return mode === "resume_from_failed_step"
		? "Resume from Failed Step"
		: "Start Over"
}

const RETRY_OPTIONS: Array<{
	mode: TAutomationRetryMode
	title: string
	description: string
	recommended?: boolean
}> = [
	{
		mode: "resume_from_failed_step",
		title: "Resume from Failed Step",
		description:
			"Create a new run, carry forward the earlier executed steps, and continue only from the failed step.",
		recommended: true,
	},
	{
		mode: "start_over",
		title: "Start Over",
		description:
			"Create a brand-new run from the beginning. Earlier side effects may run again.",
	},
]

export function RetryRunDialog({
	run,
	onSuccess,
	children,
}: {
	run: TAutomationRun
	onSuccess: () => void
	children?: (props: { open: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	const [submittingMode, setSubmittingMode] =
		useState<TAutomationRetryMode | null>(null)
	const [error, setError] = useState<string | null>(null)

	if (run.status !== "failed" || run.run_mode === "dry_run") {
		return null
	}

	async function handleRetry(mode: TAutomationRetryMode) {
		setSubmittingMode(mode)
		setError(null)
		try {
			await XHR(xhr).retryRun(run.id, mode)
			showSnackbar(`Retry started using "${retryModeLabel(mode)}".`)
			closeDialog()
			onSuccess()
		} catch (retryError) {
			const message =
				retryError instanceof Error && retryError.message
					? retryError.message
					: "Unable to retry this run right now."
			setError(message)
		} finally {
			setSubmittingMode(null)
		}
	}

	return (
		<>
			{children ? (
				children({ open: openDialog })
			) : (
				<Button size="sm" level="tertiary" onClick={openDialog} status="danger">
					Retry
				</Button>
			)}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title={`Retry Run #${run.id}`}
				md
			>
				<Dialog.Body>
					<Stack gap="4">
						<Text color="muted">
							Choose how we should create the new retry run. The original failed
							run will stay untouched for traceability.
						</Text>

						<Stack gap="3">
							{RETRY_OPTIONS.map((option) => (
								<Box key={option.mode} borderWidth="1" rounded="md" padding="4">
									<Stack gap="3">
										<Stack gap="1">
											<Inline alignItems="center" gap="2">
												<Text fontWeight="semibold">{option.title}</Text>
												{option.recommended ? (
													<Text color="muted" fontSize="sm">
														Recommended
													</Text>
												) : null}
											</Inline>
											<Text color="muted" fontSize="sm">
												{option.description}
											</Text>
										</Stack>
										<Button
											level={option.recommended ? "primary" : "secondary"}
											disabled={submittingMode !== null}
											onClick={() => handleRetry(option.mode)}
										>
											{submittingMode === option.mode
												? "Retrying..."
												: option.title}
										</Button>
									</Stack>
								</Box>
							))}
						</Stack>

						{error ? (
							<Text color="danger" fontSize="sm">
								{error}
							</Text>
						) : null}
					</Stack>
				</Dialog.Body>
				<Dialog.Footer>
					<Button
						level="secondary"
						onClick={closeDialog}
						disabled={submittingMode !== null}
					>
						Cancel
					</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}
